'use client';

import { useState, useEffect, useMemo } from 'react';
import { Stack, Text, Button, Alert, Loader } from '@mantine/core';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { IconCreditCard, IconCheck } from '@tabler/icons-react';

interface PaymentStepProps {
  onPaymentSetup: () => void;
  paymentError?: string;
}

export default function PaymentStep({ 
  onPaymentSetup, 
  paymentError 
}: PaymentStepProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const stripeJs = useMemo(
    () => loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || ''),
    []
  );

  useEffect(() => {
    let active = true;
    
    const checkExistingPayment = async () => {
      try {
        // Check if user already has valid payment details
        const checkResponse = await fetch('/api/billing/hasValidPaymentDetails');
        const checkData = await checkResponse.json();
        
        if (!active) return;
        
        if (checkData.result) {
          setSetupComplete(true);
          onPaymentSetup();
          return;
        }

        // First ensure user has a Stripe customer ID
        const customerResponse = await fetch('/api/billing/create-setup-session', { method: 'POST' });
        if (!customerResponse.ok) {
          const errorData = await customerResponse.json();
          throw new Error(errorData.error || 'Failed to set up billing');
        }
        
        // Now create setup intent for embedded form
        const response = await fetch('/api/billing/setup-intent', { method: 'POST' });
        if (!active) return;
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Setup intent failed (${response.status})`);
        }
        
        const data = await response.json();
        if (!data?.clientSecret) {
          throw new Error('Missing client secret');
        }
        
        setClientSecret(data.clientSecret);
      } catch (error) {
        if (active) {
          setLoadError(error instanceof Error ? error.message : 'Failed to setup payment');
        }
      }
    };

    checkExistingPayment();
    return () => { active = false; };
  }, [onPaymentSetup]);

  if (setupComplete) {
    return (
      <Alert color="green" variant="light" icon={<IconCheck size={16} />}>
        Payment method added successfully!
      </Alert>
    );
  }

  if (loadError) {
    return (
      <Alert color="red" variant="light">
        {loadError}
      </Alert>
    );
  }

  if (!clientSecret) {
    return (
      <Stack gap="md" align="center">
        <Loader size="md" />
        <Text size="sm" c="dimmed">Setting up secure payment...</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed" style={{ margin: 0 }}>
        Add a payment method to complete your application:
      </Text>
      
      <Text size="sm" c="dimmed">
        We use Stripe to securely process payments. You won&apos;t be charged until your mentor accepts your application.
      </Text>
      
      <Elements stripe={stripeJs} options={{ clientSecret }}>
        <PaymentForm onSuccess={onPaymentSetup} />
      </Elements>
      
      {paymentError && (
        <Alert color="red" variant="light">
          {paymentError}
        </Alert>
      )}
    </Stack>
  );
}

function PaymentForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || saving) return;

    setSaving(true);
    setError(null);

    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      setSaving(false);
      setError(error.message || 'Failed to add payment method');
      return;
    }

    // Set as default payment method
    const pmId = typeof setupIntent?.payment_method === 'string'
      ? setupIntent.payment_method
      : setupIntent?.payment_method?.id;

    if (pmId) {
      try {
        await fetch('/api/billing/default', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ paymentMethodId: pmId }),
        });
      } catch {
        // Non-fatal error
      }
    }

    setSaving(false);
    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <PaymentElement 
          options={{
            fields: {
              billingDetails: {
                address: {
                  country: 'auto', // Auto-detect or allow selection
                },
              },
            },
          }}
        />
        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}
        <Button 
          type="submit" 
          loading={saving}
          leftSection={<IconCreditCard size={16} />}
          size="md" 
          fullWidth
        >
          {saving ? 'Adding Payment Method...' : 'Add Payment Method'}
        </Button>
      </Stack>
    </form>
  );
}
